import AuthLogo from 'src/components/DriverAuth/AuthLogo'
import OtpStep from 'src/components/DriverAuth/OtpStep'
import PhoneStep from 'src/components/DriverAuth/PhoneStep'
import {
  useDriverEmbeddedAuth,
  UseDriverEmbeddedAuthOptions,
} from 'src/components/DriverAuth/useDriverEmbeddedAuth'
import TurnstileWidget from 'src/components/Turnstile/TurnstileWidget'

interface DriverEmbeddedAuthProps extends UseDriverEmbeddedAuthOptions {
  /** Optional logo override; defaults to the Double Nickel mark. */
  logoSrc?: string
  /** Company name — labels the logo and shows if it fails to load. */
  companyName?: string
  /** Company branding still resolving — keeps the default mark from flashing. */
  logoLoading?: boolean
  /** When provided (alongside a company name), shows a "See all jobs from
   *  {company}" link to this href under Continue on the phone step. */
  seeJobsHref?: string
}

/**
 * Self-contained embedded SMS auth widget: branded card, phone + OTP steps, and
 * the auth state machine. Drop it anywhere and wire onVerified / onNewUser to
 * decide navigation. Pass initialPhone + autoStart to jump straight to verify.
 */
const DriverEmbeddedAuth = ({
  logoSrc,
  companyName,
  logoLoading,
  seeJobsHref,
  ...authOptions
}: DriverEmbeddedAuthProps) => {
  const {
    step,
    phone,
    otp,
    loading,
    error,
    canSubmitPhone,
    cooldownSeconds,
    phoneBlocked,
    setPhone,
    setOtp,
    sendCode,
    verifyCode,
    editNumber,
    turnstile,
  } = useDriverEmbeddedAuth(authOptions)

  return (
    <div className="w-full max-w-sm min-[601px]:rounded-2xl min-[601px]:bg-white min-[601px]:p-8 min-[601px]:shadow-sm">
      <AuthLogo
        src={logoSrc}
        name={companyName}
        loading={logoLoading}
        className="mb-6"
      />

      {step === 'phone' && (
        <PhoneStep
          phone={phone}
          loading={loading}
          verifying={turnstile.verifying}
          canSubmit={canSubmitPhone}
          cooldownSeconds={phoneBlocked ? cooldownSeconds : 0}
          onPhoneChange={setPhone}
          onSubmit={sendCode}
          seeJobsLabel={
            seeJobsHref && companyName
              ? `See all jobs from ${companyName}`
              : undefined
          }
          seeJobsHref={seeJobsHref}
        />
      )}

      {step === 'otp' && (
        <OtpStep
          phone={phone}
          otp={otp}
          loading={loading}
          verifying={turnstile.verifying}
          cooldownSeconds={cooldownSeconds}
          onOtpChange={setOtp}
          onSubmit={() => verifyCode()}
          onResend={sendCode}
          onEdit={editNumber}
        />
      )}

      {/* Mounted across both steps so "Resend code" on the OTP step can mint a
          fresh token. Invisible unless Cloudflare needs an interactive check or
          a token wait expired (then `visible` forces it on screen so the driver
          can complete it); renders nothing when no site key is configured. */}
      <TurnstileWidget
        ref={turnstile.widgetRef}
        {...turnstile.widgetProps}
        className="mt-4 empty:hidden"
      />

      {error && (
        <div className="mt-4 text-center text-sm text-doubleNickel-error-500">
          {error}
        </div>
      )}
    </div>
  )
}

export default DriverEmbeddedAuth
